import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Box,
  Stack,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  Divider,
  Skeleton,
} from '@mui/material';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

const MetricBar = ({ label, percent, used, total, free, color = 'primary', showDetails = true }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
      <Typography variant="body2" fontWeight={600}>{label}</Typography>
      <Typography variant="caption" color="text.secondary">{percent.toFixed(1)}%</Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={percent > 100 ? 100 : percent}
      color={color}
      sx={{ height: 8, borderRadius: 4 }}
    />
    {showDetails && (
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">Used: {formatBytes(used)}</Typography>
        <Typography variant="caption" color="text.secondary">Free: {formatBytes(free)}</Typography>
        <Typography variant="caption" color="text.secondary">Total: {formatBytes(total)}</Typography>
      </Stack>
    )}
  </Box>
);

const AdminServerHealth = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHealth = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/serverhealth');
        const data = await res.json();
        setHealth(data);
      } catch (err) {
        setError('Failed to fetch server health');
      } finally {
        setLoading(false);
      }
    };
    fetchHealth();
  }, []);

  // Memory
  const totalMem = health?.system?.totalmem ?? 0;
  const freeMem = health?.system?.freemem ?? 0;
  const usedMem = totalMem - freeMem;
  const memPercent = totalMem ? (usedMem / totalMem) * 100 : 0;

  // CPU
  let cpuPercent = 0;
  if (health?.process?.cpu) {
    const totalMicros = (health.process.cpu.user + health.process.cpu.system);
    const uptimeSec = health.process.uptime || 1;
    cpuPercent = uptimeSec ? (totalMicros / 10000) / uptimeSec : 0;
    if (cpuPercent > 100) cpuPercent = 100;
  }

  // Disk
  const totalDisk = health?.system?.disk?.total ?? 0;
  const freeDisk = health?.system?.disk?.free ?? 0;
  const usedDisk = health?.system?.disk?.used ?? 0;
  const diskPercent = totalDisk ? (usedDisk / totalDisk) * 100 : 0;

  const rpm = health?.metrics?.rpm ?? '—';
  const errorRate = health?.metrics?.errorRate ?? '—';
  const avgResponseTime = health?.metrics?.avgResponseTime;
  const activeConnections = health?.metrics?.activeConnections ?? '—';

  const isHealthy = health?.status === 'ok' || health?.status === 'healthy' || health?.status === 'UP';
  const dbOk = health?.database?.status === 'connected' || health?.database?.status === 'ok';

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
      <CardHeader
        title="Server Health"
        subheader="Real-time server resource and performance metrics"
        titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
        action={
          !loading && health && (
            <Chip
              label={isHealthy ? 'Operational' : 'Degraded'}
              color={isHealthy ? 'success' : 'warning'}
              size="small"
              sx={{ mt: 1.5, mr: 1.5, fontWeight: 700 }}
            />
          )
        }
      />
      {loading && <LinearProgress color="primary" />}
      <CardContent>
        {error && <Alert severity="error">{error}</Alert>}

        {loading && (
          <Stack spacing={3}>
            {[1, 2, 3].map((i) => (
              <Box key={i}>
                <Skeleton variant="text" width={120} />
                <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 4, mt: 1 }} />
              </Box>
            ))}
          </Stack>
        )}

        {!loading && !error && health && (
          <Stack spacing={3}>
            {/* Status row */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DnsOutlinedIcon color={isHealthy ? 'success' : 'warning'} fontSize="small" />
                  <Typography variant="body2">
                    <strong>API Server:</strong>{' '}
                    <span style={{ textTransform: 'capitalize' }}>{health.status || 'Unknown'}</span>
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <StorageOutlinedIcon color={dbOk ? 'success' : 'error'} fontSize="small" />
                  <Typography variant="body2">
                    <strong>Database:</strong> {health.database?.status || 'Unknown'}
                    {health.database?.latency && ` (Latency: ${health.database.latency} ms)`}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>

            <Divider />

            {/* Resource bars */}
            <MetricBar
              label="Memory Usage"
              percent={memPercent}
              used={usedMem}
              total={totalMem}
              free={freeMem}
              color="primary"
            />
            <MetricBar
              label="CPU Usage"
              percent={cpuPercent}
              used={0}
              total={0}
              free={0}
              color="success"
              showDetails={false}
            />
            <MetricBar
              label="Disk Usage"
              percent={diskPercent}
              used={usedDisk}
              total={totalDisk}
              free={freeDisk}
              color="secondary"
            />

            <Divider />

            {/* Metrics grid */}
            <Grid container spacing={2}>
              {[
                { label: 'Requests / min', value: rpm, icon: <SpeedOutlinedIcon fontSize="small" /> },
                { label: 'Error Rate / min', value: errorRate, icon: <HealthAndSafetyOutlinedIcon fontSize="small" /> },
                { label: 'Avg Response Time', value: avgResponseTime?.toFixed ? `${avgResponseTime.toFixed(1)} ms` : (avgResponseTime ?? '—'), icon: <MemoryOutlinedIcon fontSize="small" /> },
                { label: 'Active Connections', value: activeConnections, icon: <DnsOutlinedIcon fontSize="small" /> },
              ].map(({ label, value, icon }) => (
                <Grid item xs={6} key={label}>
                  <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ color: 'primary.main' }}>{icon}</Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="subtitle2" fontWeight={700}>{String(value)}</Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Footer */}
            <Typography variant="caption" color="text.secondary">
              Platform: {health.system?.platform} ({health.system?.arch}) &bull;
              CPUs: {health.system?.cpus} &bull;
              Uptime: {health.process?.uptime ? `${health.process.uptime.toFixed(0)}s` : '—'} &bull;
              {health.timestamp && ` As of ${new Date(health.timestamp).toLocaleString()}`}
            </Typography>
          </Stack>
        )}

        {!loading && !error && !health && (
          <Typography color="text.secondary">No health data available.</Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminServerHealth;

